import type { MarketMonitorSourceScope } from './index';
import { isDiscoveryDateCapability, normalizeDiscoveryDates, type DiscoveryDates } from './discoveryDates';

export const NATIVE_HISTORY_SUPPORT = 'native-date-history/v1';
export const NATIVE_HISTORY_PLAN = 'market-native-date-history-plan/v1';
export interface NativeDateHistoryPolicy {
    schema_version: typeof NATIVE_HISTORY_SUPPORT;
    mode: 'incremental_recheck';
    overlap_days: number;
    max_window_days: number;
    time_zone?: string;
}
export interface NativeDateHistoryPlan {
    contract_version: typeof NATIVE_HISTORY_PLAN;
    capability: string;
    evaluated_at: string;
    initial_start_date: string;
    initial_end_date: string;
    overlap_days: number;
    max_window_days: number;
    time_zone?: string;
    coverage_semantics: 'observed_sample_only';
    gap_codes: string[];
    checkpoint_job_id?: string;
    requested_since: string;
    slice_id: string;
    input: string;
    phase: 'initial_history' | 'history_catch_up' | 'incremental_recheck' | 'gap_recheck' | 'needs_review';
    action: 'collect' | 'confirm_scope';
    resolved_start_date?: string;
    resolved_end_date?: string;
    reason_code?: 'history_time_unverified' | 'history_submit_outcome_unresolved' | 'history_gap_needs_review' | 'history_dispatch_window_expired';
    dispatch_valid_until?: string;
    dispatched_at?: string;
}
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const daysBetween = (start: string, end: string) => (Date.parse(end) - Date.parse(start)) / 86400000;
const timestamp = (value: unknown): value is string => typeof value === 'string' && /(?:Z|[+-]\d{2}:\d{2})$/.test(value) && Number.isFinite(Date.parse(value));
export const needsNativeHistoryPreview = (requested: MarketMonitorSourceScope[], previous: MarketMonitorSourceScope[] = []) =>
    [...requested, ...previous].some(scope => isDiscoveryDateCapability(scope.capability) && scope.collection_history !== undefined);

export function normalizeNativeDateHistory(value: unknown, dates: DiscoveryDates): NativeDateHistoryPolicy | null {
    if (value == null) return null;
    const pair = normalizeDiscoveryDates(dates);
    const allowed = new Set(['schema_version', 'mode', 'overlap_days', 'max_window_days', 'time_zone']);
    if (!record(value) || ![4, 5].includes(Object.keys(value).length) || Object.keys(value).some(key => !allowed.has(key))
        || value.schema_version !== NATIVE_HISTORY_SUPPORT
        || value.mode !== 'incremental_recheck' || !Number.isInteger(value.overlap_days)
        || Number(value.overlap_days) < 1 || Number(value.overlap_days) > 30 || !Number.isInteger(value.max_window_days)
        || Number(value.max_window_days) < 2 || Number(value.max_window_days) > 3660
        || Number(value.max_window_days) <= Number(value.overlap_days) || !pair.discovery_start_date || !pair.discovery_end_date
        || daysBetween(pair.discovery_start_date, pair.discovery_end_date) > Number(value.max_window_days))
        throw new Error('Invalid native date history policy');
    const timeZone = value.time_zone;
    if (timeZone !== undefined) {
        if (typeof timeZone !== 'string') throw new Error('Invalid native date history policy');
        try { new Intl.DateTimeFormat('en', { timeZone }).format(); }
        catch { throw new Error('Invalid native date history policy'); }
    }
    return { schema_version: NATIVE_HISTORY_SUPPORT, mode: 'incremental_recheck', overlap_days: Number(value.overlap_days),
        max_window_days: Number(value.max_window_days), ...(timeZone ? { time_zone: timeZone } : {}) };
}

export function validNativeDateHistoryPlan(value: unknown): value is NativeDateHistoryPlan {
    if (!record(value) || value.contract_version !== NATIVE_HISTORY_PLAN || typeof value.capability !== 'string'
        || !isDiscoveryDateCapability(value.capability) || !timestamp(value.evaluated_at) || !timestamp(value.requested_since)
        || value.coverage_semantics !== 'observed_sample_only' || typeof value.slice_id !== 'string' || !value.slice_id.trim()
        || typeof value.input !== 'string' || !value.input.trim() || !Array.isArray(value.gap_codes) || !value.gap_codes.every(code => typeof code === 'string')
        || value.checkpoint_job_id !== undefined && typeof value.checkpoint_job_id !== 'string'
        || value.time_zone !== undefined && (typeof value.time_zone !== 'string' || !validTimeZone(value.time_zone))
        || value.dispatched_at !== undefined && !timestamp(value.dispatched_at)) return false;
    try {
        normalizeNativeDateHistory({ schema_version: NATIVE_HISTORY_SUPPORT, mode: 'incremental_recheck',
            overlap_days: value.overlap_days, max_window_days: value.max_window_days }, {
            discovery_start_date: value.initial_start_date as string, discovery_end_date: value.initial_end_date as string });
        if (value.action === 'confirm_scope') return value.phase === 'needs_review'
            && ['history_time_unverified', 'history_submit_outcome_unresolved', 'history_gap_needs_review', 'history_dispatch_window_expired'].includes(String(value.reason_code));
        const resolved = normalizeDiscoveryDates({ discovery_start_date: value.resolved_start_date as string, discovery_end_date: value.resolved_end_date as string });
        return value.action === 'collect' && ['initial_history', 'history_catch_up', 'incremental_recheck', 'gap_recheck'].includes(String(value.phase))
            && !!resolved.discovery_start_date && !!resolved.discovery_end_date && timestamp(value.dispatch_valid_until)
            && resolved.discovery_start_date >= String(value.initial_start_date)
            && daysBetween(resolved.discovery_start_date, resolved.discovery_end_date) <= Number(value.max_window_days);
    } catch { return false; }
}

/** Policy removal is negotiated against saved scopes, including a change to specified-video mode. */
export function nativeHistoryScopesMatch(requested: MarketMonitorSourceScope[], echoed: unknown, previous: MarketMonitorSourceScope[] = []): boolean {
    const active = needsNativeHistoryPreview(requested, previous);
    if (!active) return true;
    if (!Array.isArray(echoed)) return false;
    const entries = (scopes: MarketMonitorSourceScope[]) => scopes.filter(scope => isDiscoveryDateCapability(scope.capability)).map(scope =>
        JSON.stringify([scope.capability, scope.source_system, scope.scope.kind, scope.scope.value ?? null,
            normalizeDiscoveryDates(scope), normalizeNativeDateHistory(scope.collection_history, scope)])).sort();
    try {
        const expected = entries(requested), received = entries(echoed);
        return new Set(expected).size === expected.length && new Set(received).size === received.length
            && JSON.stringify(expected) === JSON.stringify(received);
    } catch { return false; }
}

export function nativeHistoryPreviewMatches(requested: MarketMonitorSourceScope[], preview: {
    normalized_monitor: Record<string, unknown>; native_date_history_support?: unknown; collection_history_preview?: unknown;
}, previous: MarketMonitorSourceScope[] = []): boolean {
    if (!needsNativeHistoryPreview(requested, previous)) return true;
    if (preview.native_date_history_support !== NATIVE_HISTORY_SUPPORT
        || !nativeHistoryScopesMatch(requested, preview.normalized_monitor.source_scopes, previous)) return false;
    const plans = Array.isArray(preview.collection_history_preview)
        ? preview.collection_history_preview.filter(plan => record(plan) && plan.contract_version === NATIVE_HISTORY_PLAN) : [];
    if (!plans.every(validNativeDateHistoryPlan) || new Set(plans.map(plan => plan.slice_id)).size !== plans.length) return false;
    const scopes = requested.filter(scope => isDiscoveryDateCapability(scope.capability) && scope.collection_history != null);
    const terms = preview.normalized_monitor.terms, identifiers = preview.normalized_monitor.identifiers;
    let count = 0;
    for (const scope of scopes) {
        const keyword = scope.capability === 'youtube.videos.discover_by_keyword';
        const values = keyword ? terms : identifiers;
        if (!Array.isArray(values) || !values.every(record)) return false;
        const inputs = [...new Set(values.filter(item => keyword ? item.role !== 'exclude' : item.source_system === scope.source_system
            && (item.kind === 'source_url' || scope.source_system === 'instagram' && item.kind === 'social_account')).map(item => item.value))];
        if (!inputs.every(input => typeof input === 'string' && input.trim())) return false;
        const sourcePlans = plans.filter(plan => plan.capability === scope.capability);
        const policy = normalizeNativeDateHistory(scope.collection_history, scope)!;
        count += inputs.length;
        if (sourcePlans.length !== inputs.length || !inputs.every(input => sourcePlans.filter(plan => plan.input === input).length === 1)
            || sourcePlans.some(plan => plan.initial_start_date !== scope.discovery_start_date || plan.initial_end_date !== scope.discovery_end_date
                || plan.overlap_days !== policy.overlap_days || plan.max_window_days !== policy.max_window_days
                || (plan.time_zone ?? 'UTC') !== (policy.time_zone ?? 'UTC'))) return false;
    }
    return count === plans.length;
}

function validTimeZone(value: string): boolean {
    try { new Intl.DateTimeFormat('en', { timeZone: value }).format(); return true; }
    catch { return false; }
}
